import React, { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import { Button, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { generateClient } from "aws-amplify/api";
import { listContents } from "../../graphql/queries";
import { deleteContent } from "../../graphql/mutations";
import { useNavigate, useParams } from "react-router-dom";
import { Delete, Edit } from "@mui/icons-material";
import Label from "../../components/Label";
import CustomDataGrid from "../../components/CustomDataGrid";
import Box from "@mui/material/Box";
import moment from "moment";
import ActionsPopover from "../../components/actionsPopover";import CreateContentModal from "../../components/Content/CreateContentModal";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { get } from "aws-amplify/api";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  dataGridContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  },
  dataGrid: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      "& .MuiDataGrid-columnHeaders": {
        display: "none",
      },
      "& .MuiDataGrid-cell": {
        display: "block",
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1, 2),
        "&:not(:last-child)": {
          borderBottom: 0,
        },
        "&:before": {
          content: "attr(data-field)",
          fontWeight: "bold",
          marginBottom: theme.spacing(1),
        },
      },
    },
  },
}));

const ContentsPerBatch = () => {
  const navigate = useNavigate();
  const classes = useStyles();
  const client = generateClient();
  const [contentList, setContentList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const [providerData, setProviderData] = useState({});
  useEffect(() => {
    fetchAllContents();
  }, [success]);

  const params = useParams();
  const fetchAllContents = async () => {
    try {
      setLoading(true);
      const response = await client.graphql({
        query: listContents,
        variables: {
          filter: {
            seriesId: {
              eq: params?.id,
            },
          },
        },
      });
      const contentList = response.data.listContents.items;

      if (user.role === "Admin") {
        const providers = await Promise.all(
          contentList.map(async (content) => {
            if (content.owner) {
              const providerResponse = await fetchContentProvider(
                content.owner
              );
              return {
                id: content.owner,
                name: providerResponse
                  ? providerResponse["custom:companyName"]
                  : "Unknown",
              };
            }
            return { id: content.owner, name: "Unknown" };
          })
        );
        const providerMap = providers.reduce((acc, provider) => {
          acc[provider.id] = provider.name;
          return acc;
        }, {});
        setProviderData(providerMap);
      }

      setContentList(contentList);
    } catch (error) {
      console.error("Error fetching category:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContentProvider = async (contentProviderId) => {
    try {
      const restOperation = get({
        apiName: "restapi123",
        path: "/getUserData",
        options: {
          queryParams: {
            username: contentProviderId,
          },
        },
      });
      const { body } = await restOperation.response;
      const response = await body.json();
      return response;
    } catch (err) {
      console.log(err, "error");
    } finally {
    }
  };

  const columns = [
    { field: "title", headerName: "Title", flex: 1 },
    { field: "type", headerName: "Type", flex: 1 },
    { field: "category", headerName: "Category", flex: 1 },
    { field: "sequence", headerName: "Sequence", flex: 1 },

    { field: "currentCycle", headerName: "Current Cycle" },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <div>
          {params?.value === "Feedback"
            ? (user.role === "Admin" && "Sent Feedback") ||
              (user.role === "ContentProviders" && "Received Feedback")
            : params?.value}
        </div>
      ),
    },
    {
      field: "agreement.daysOfAgreement",
      headerName: "Term Of Agreement",
      flex: 1,
      valueGetter: (params) => params.row.agreement?.daysOfAgreement,
      renderCell: (params) => <div>{params.value / 30} months</div>,
    },
    {
      field: "createdAt",
      headerName: "Upload Request",
      flex: 1,
      renderCell: (params) => (
        <div>{moment(params?.value).format("DD MMM YYYY")}</div>
      ),
    },
    {
      field: "releaseDate",
      headerName: "Uploaded",
      flex: 1,
      renderCell: (params) => (
        <div>
          {params?.value
            ? moment(params?.value).format("DD MMM YYYY")
            : "Pending"}
        </div>
      ),
    },

    {
      field: "actions",
      headerName: "Edit Content",

      renderCell: (params) => (
        <ActionsPopover
          itemId={params.row.id}
          viewPath={`/content/${params.row.id}`}
          editPath={`/content/edit/${params.row.id}`}
          canDelete={true}
          onDelete={deleteContentById}
          canEdit={user.role === "ContentProviders"}
        />
      ),
    },
  ];

  if (user?.role === "Admin") {
    const contentProviderColumn = {
      field: "owner",
      headerName: "Content Provider",
      flex: 1,
      renderCell: (params) => (
        <Typography>{providerData[params.row.owner] || "Unknown"}</Typography>
      ),
    };

    // Insert the column in the second last position
    columns.splice(columns.length - 1, 0, contentProviderColumn);
  }

  const updateContentById = async (contentId) => {
    navigate(`/content/update`, {
      state: {
        userId: contentId,
      },
    });
  };

  const deleteContentById = async (contentId) => {
    try {
      const response = await client.graphql({
        query: deleteContent,
        variables: { input: { id: contentId } },
      });
      if (response.data.deleteContent) {
        toast.success("Deleted Successfully!");
        setSuccess(!success);
      }
    } catch (err) {
      console.log("Error deleting Category:", err);
      toast.error(err.message);
    }
  };

  return (
    <>
      {user?.role === "ContentProviders" && <CreateContentModal />}
      <Box marginTop={2}>
        <div className={classes.dataGridContainer}>
          <CustomDataGrid
            loading={loading}
            slotProps={{
              loadingOverlay: {
                variant: "linear-progress",
                noRowsVariant: "skeleton",
              },
            }}
            rows={contentList}
            columns={columns}
            pageSize={10}
          />
        </div>
      </Box>
    </>
  );
};

export default ContentsPerBatch;
